/**
 * Hook for fetching translations from the v2 translations API
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchTranslations } from "@/api/translations";
import type { LocalizedTexts } from "@/types/layout";

interface UseTranslationsResult {
  translations: LocalizedTexts;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useTranslations(): UseTranslationsResult {
  const [translations, setTranslations] = useState<LocalizedTexts>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const result = await fetchTranslations(controller.signal);

    if (controller.signal.aborted) return;

    if (result.ok) {
      setTranslations(result.data);
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();

    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  return {
    translations,
    isLoading,
    error,
    refetch: load,
  };
}
